/**
 * Layer chains of the summed line properties (W1-05a engine-eval; rpe-format.md F§6.2-6.3, R§2.1-2.2,
 * DESIGN.md §6.2, ARCHITECTURE.md §7.1).
 *
 * `events_with_factor`: the participating lists of a property are those of the non-null `eventLayers` slots that
 * carry the list (in slot order). Each list is an AnimF32 with the property factor applied per keyframe in f32;
 * the chain value is their RIGHT-associative f32 sum `l0 + (l1 + (l2 + l3))` (`F32.sumChain`). No participating
 * list = the constant 0 (alpha 0: an invisible line whose notes still draw). The IR cannot carry `[]` lists
 * (normalisation turns Phira's empty-array cases into their rendered equivalents), so these are all the cases.
 *
 * Preview filters: a muted layer (`${prop}:${slot}` in `PreviewFilters.mutedLayers`) is left out of the sum at
 * evaluation time; muting every participating layer behaves like "no list" (0). Mute never changes exports.
 *
 * Display: `channelValue` readouts use the same chains compiled with factor 1 (RPE units: alpha 0..255, px,
 * clockwise degrees, RPE speed), per slot or summed; NaN when nothing participates.
 *
 * Internal API (W1-05b, lineState.ts):
 *   compileLayerChain(eventLayers, prop, env, factor?)   chain of one property
 *   chain.valueAt(t, muted?)                             Phira value (factor applied)
 *   chain.displayAt(t, layer | 'sum')                    readout value (use a factor-1 chain)
 *   PROP_FACTOR_F32, SPEED_RATIO_F32, LAYER_LIST_KEY, muteKey
 */

import { F32, F32_FACTORS } from '../math/f32';
import type { IrEventList, IrLayer } from '../ir/types';
import type { LayeredProp } from '../model/types';
import { AnimF32, type EvalEnv, type NumberTrackOptions } from './anim';

const fr = Math.fround;

/** Phira's SPEED_RATIO = 10 / 45 / HEIGHT_RATIO as the f32 constant expression (0.26717430353164673). */
export const SPEED_RATIO_F32 = fr(fr(10 / 45) / fr(0.83175));

/** Per-keyframe f32 factors of the summed properties (moveY is divided by the aspect ratio later, where used). */
export const PROP_FACTOR_F32: Readonly<Record<LayeredProp, number>> = Object.freeze({
  alpha: F32_FACTORS.alpha,
  moveX: F32_FACTORS.moveX,
  moveY: F32_FACTORS.moveY,
  rotate: F32_FACTORS.rotate,
  speed: SPEED_RATIO_F32,
});

/** Event list key of each summed property in an `eventLayers` slot. */
export const LAYER_LIST_KEY = Object.freeze({
  alpha: 'alphaEvents',
  moveX: 'moveXEvents',
  moveY: 'moveYEvents',
  rotate: 'rotateEvents',
  speed: 'speedEvents',
} as const satisfies Record<LayeredProp, keyof IrLayer>);

/** The `PreviewFilters.mutedLayers` entry of a (property, slot). */
export function muteKey(prop: LayeredProp, slot: number): string {
  return `${prop}:${String(slot)}`;
}

/** The list of a property in a layer slot. */
export function layerList(layer: IrLayer | null, prop: LayeredProp): IrEventList<number> | undefined {
  return layer === null ? undefined : layer[LAYER_LIST_KEY[prop]];
}

export interface ChainLayer {
  /** eventLayers slot index (= the model's layer index). */
  readonly slot: number;
  readonly anim: AnimF32;
  readonly muteKey: string;
}

export class LayerChain {
  readonly prop: LayeredProp;
  /** Participating lists in slot order. */
  readonly layers: readonly ChainLayer[];
  readonly factor: number;
  private readonly scratch: Float64Array;

  constructor(prop: LayeredProp, layers: readonly ChainLayer[], factor: number) {
    this.prop = prop;
    this.layers = layers;
    this.factor = factor;
    this.scratch = new Float64Array(layers.length);
  }

  /** No layer carries the list: Phira's constant 0. */
  get absent(): boolean {
    return this.layers.length === 0;
  }

  /** Phira's value at t: right-associative f32 sum of the unmuted participating layers; 0 when none. */
  valueAt(t: number, muted?: ReadonlySet<string> | null): number {
    const layers = this.layers;
    const n = layers.length;
    if (n === 0) return 0;
    if (muted === undefined || muted === null) {
      // The frame path (no preview mute): one layer is the common case.
      if (n === 1) return (layers[0] as ChainLayer).anim.valueAt(t);
      const v = this.scratch;
      for (let k = 0; k < n; k++) v[k] = (layers[k] as ChainLayer).anim.valueAt(t);
      return F32.sumChain(v, n);
    }
    const v = this.scratch;
    let count = 0;
    for (const l of layers) {
      if (muted.has(l.muteKey)) continue;
      v[count++] = l.anim.valueAt(t);
    }
    if (count === 0) return 0;
    if (count === 1) return v[0] ?? 0;
    return F32.sumChain(v, count);
  }

  /** Value of one slot (NaN when the slot carries no list). */
  layerValueAt(slot: number, t: number): number {
    for (const l of this.layers) if (l.slot === slot) return l.anim.valueAt(t);
    return Number.NaN;
  }

  /** Readout: a slot's value or the sum over every participating slot; NaN when nothing participates. */
  displayAt(t: number, layer: number | 'sum'): number {
    if (layer !== 'sum') return this.layerValueAt(layer, t);
    if (this.layers.length === 0) return Number.NaN;
    return this.valueAt(t, null);
  }

  /** The slot whose list holds the active keyframe at t, with its keyframe cursor (segment lookup helpers). */
  cursorOf(slot: number, t: number): { readonly anim: AnimF32; readonly cursor: number } | null {
    for (const l of this.layers) if (l.slot === slot) return { anim: l.anim, cursor: l.anim.cursorAt(t) };
    return null;
  }

  usesBezier(): boolean {
    return this.layers.some((l) => l.anim.usesBezier());
  }
}

/**
 * Chain of one summed property over a line's `eventLayers` (null slots skipped). `factor` defaults to the
 * property's Phira factor; pass 1 for RPE-unit readouts. `opts` may override the event tween (legacy speed).
 */
export function compileLayerChain(
  eventLayers: readonly (IrLayer | null)[],
  prop: LayeredProp,
  env: EvalEnv,
  factor: number = PROP_FACTOR_F32[prop],
  opts: Omit<NumberTrackOptions, 'factor' | 'default'> = {},
): LayerChain {
  const layers: ChainLayer[] = [];
  eventLayers.forEach((layer, slot) => {
    const list = layerList(layer, prop);
    if (list === undefined) return;
    layers.push({
      slot,
      anim: AnimF32.fromEvents(list, env, { ...opts, factor }),
      muteKey: muteKey(prop, slot),
    });
  });
  return new LayerChain(prop, layers, fr(factor));
}
